import React, { createContext, useContext, useEffect, useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { usePublicTheme } from '../../hooks/usePublicTheme';
import { DemoRequestModal, usePlatformDemo } from './DemoRequestModal';
import { BrandLogo, Pill, ThemeSwitch } from './VitrineUi';

/** Les pages du site vitrine : une page par métier, plus l'accueil et les formules. */
export const PAGES_VITRINE = [
    { to: '/', label: 'Accueil', fin: true },
    { to: '/metiers/restaurateur', label: 'Restaurateurs' },
    { to: '/metiers/prestataire', label: 'Prestataires' },
    { to: '/metiers/fournisseur', label: 'Fournisseurs' },
    { to: '/formules', label: 'Formules' },
];

const DemoCtx = createContext<(size?: 'md' | 'lg', label?: string) => React.ReactNode>(() => null);
/** Bouton « Demander une démo » du site (rien si le super admin n'a pas activé le widget RDV Pro). */
export const useDemoButton = () => useContext(DemoCtx);

/** Coque commune des pages du site vitrine : en-tête avec la navigation, pied de page, fenêtre de démo. */
export const VitrineLayout: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => {
    const { isDark, toggleTheme } = usePublicTheme();
    const demo = usePlatformDemo();
    const [demoOpen, setDemoOpen] = useState(false);

    useEffect(() => {
        const previousTitle = document.title;
        document.title = title;
        window.scrollTo(0, 0);
        return () => { document.title = previousTitle; };
    }, [title]);

    const demoButton = (size: 'md' | 'lg' = 'md', label?: string) => demo.clientId
        ? <Pill size={size} onClick={() => setDemoOpen(true)}>{label || demo.label}</Pill>
        : null;
    const lien = ({ isActive }: { isActive: boolean }) => `whitespace-nowrap rounded-full px-4 py-2 text-[14px] font-semibold transition-colors ${isActive ? 'bg-content text-surface' : 'text-content-muted hover:text-content hover:bg-surface-soft'}`;

    return (
        <DemoCtx.Provider value={demoButton}>
            <div className="min-h-screen bg-surface text-content" style={{ fontFamily: '"Instrument Sans", ui-sans-serif, system-ui, sans-serif' }}>
                <header className="sticky top-0 z-40 bg-surface/85 backdrop-blur border-b border-border">
                    <div className="max-w-[1160px] mx-auto px-5 h-[68px] flex items-center justify-between gap-4">
                        <div className="flex items-center gap-8 min-w-0">
                            <Link to="/" className="shrink-0"><BrandLogo className="h-6" /></Link>
                            <nav className="hidden lg:flex items-center gap-1 rounded-full border border-border bg-surface p-1" aria-label="Pages du site">
                                {PAGES_VITRINE.map((p) => <NavLink key={p.to} to={p.to} end={p.fin} className={lien}>{p.label}</NavLink>)}
                            </nav>
                        </div>
                        <div className="flex items-center gap-3">
                            <ThemeSwitch isDark={isDark} onToggle={toggleTheme} />
                            <span className="hidden sm:block"><Pill variant="ghost" to="/login">Se connecter</Pill></span>
                            {demoButton()}
                        </div>
                    </div>
                    {/* Téléphone et tablette : la même navigation, sur une ligne qui défile */}
                    <nav className="lg:hidden border-t border-border overflow-x-auto" aria-label="Pages du site">
                        <div className="flex gap-1 px-4 py-2 w-max">
                            {PAGES_VITRINE.map((p) => <NavLink key={p.to} to={p.to} end={p.fin} className={lien}>{p.label}</NavLink>)}
                        </div>
                    </nav>
                </header>

                <main id="top">{children}</main>

                <footer className="border-t border-border">
                    <div className="max-w-[1160px] mx-auto px-5 min-h-[72px] py-4 flex flex-wrap items-center justify-between gap-4">
                        <BrandLogo className="h-5 opacity-60" />
                        <nav className="flex flex-wrap gap-x-6 gap-y-2 text-[12.5px] text-content-muted">
                            <Link to="/legal" className="hover:text-content">Mentions légales</Link>
                            <Link to="/privacy" className="hover:text-content">Confidentialité</Link>
                            <Link to="/login" className="hover:text-content">Se connecter</Link>
                        </nav>
                    </div>
                </footer>

                {demo.clientId && <DemoRequestModal open={demoOpen} onClose={() => setDemoOpen(false)} clientId={demo.clientId} title={demo.label} />}
            </div>
        </DemoCtx.Provider>
    );
};
